import { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import * as Yup from "yup";

import { premiseNamesForTimeShift, regexForName } from "common/constant";

import { getConsumerNumberValidation } from "utils/getConsumerNumberValidation";
import { getDocumentsValidationSchema } from "utils/getDocumentsValidationSchema";

import { usePremiseTypeQuery } from "apis/PremiseType";
import { useApplicantTypeQuery } from "apis/ApplicantType";

const premisesForHP = ["temporary"];

const useValidationSchema = () => {
  const { data: { data: { items: applicantType = [] } = {} } = {} } =
    useApplicantTypeQuery();
  const [premiseIdsForHP, setPremiseIdsForHP] = useState([]);
  const [premiseIdsForTimeShift, setPremiseIdsForTimeShift] = useState([]);

  const formData = useSelector((state) => state.changeOfCategory);

  const { data: { data: premiseType } = {} } = usePremiseTypeQuery({
    utilityId: 1,
  });

  useEffect(() => {
    if (premiseType) {
      const updatedPremiseIdsForTimeShift = [];
      const updatedPremiseIds = [];
      premiseType.forEach((premise) => {
        if (premiseNamesForTimeShift.includes(premise.name.toLowerCase())) {
          updatedPremiseIdsForTimeShift.push(premise.id);
        }
        if (premisesForHP.includes(premise.name.toLowerCase())) {
          updatedPremiseIds.push(premise.id);
        }
      });

      setPremiseIdsForTimeShift(updatedPremiseIdsForTimeShift);
      setPremiseIdsForHP(updatedPremiseIds);
    }
  }, [premiseType]);

  const validationSchema = useMemo(
    () => [
      Yup.object({
        utilityConsumerId: Yup.string().required("Customer number is required"),
        utilityAddress: Yup.object({
          premiseTypeId: Yup.string().required("Premise type is required"),
          premiseUsagePurpose: Yup.string().required(
            "Premise usage purpose is required"
          ),
        }),
        connectionLoad: Yup.object({
          loadDetails: Yup.string().required("Load details is required"),
          supplyVoltage: Yup.string().required("Supply voltage is required"),
          tention: Yup.string().required("Please select power requirement"),

          timeShift: premiseIdsForTimeShift.includes(
            formData?.utilityAddress?.premiseTypeId
          )
            ? Yup.string().required("Time shift is required")
            : Yup.string(),
          isGreenPowerRequired: Yup.boolean(),
        }),
        applicant: Yup.object({
          panNumber: Yup.string()
            .matches(
              /^[A-Za-z]{5}[0-9]{4}[A-Za-z]{1}$/,
              "Please enter a valid Pan number"
            )
            .required("PAN number is required"),
          isGSTRegistered: Yup.string().required("GST is required"),
          gstNumber: Yup.string()
            .test("isGstRequired", "", function (_, context) {
              const [{ value: { isGSTRegistered, gstNumber } } = {}] =
                context.from || [];
              if (isGSTRegistered && !gstNumber) {
                return context.createError({
                  message: "GST number is required",
                });
              }
              return true;
            })
            .matches(
              /^[0-9]{2}[A-Za-z]{5}[0-9]{4}[A-Za-z]{1}[1-9A-Za-z]{1}[Zz][0-9A-Za-z]{1}$/,
              "Please enter a valid GST number"
            ),
        }),
      }),
      Yup.object({
        authorizedPerson: Yup.object({
          salutation: Yup.string().required("Salutation is required"),
          firstName: Yup.string()
            .matches(regexForName, "Please enter a valid first name")
            .required("First name is required"),
          middleName: Yup.string().matches(
            regexForName,
            "Please enter a valid middle name"
          ),
          lastName: Yup.string().matches(
            regexForName,
            "Please enter a valid last name"
          ),
          // gender: Yup.string().required("Gender is required"),
          // ageCategory: Yup.string().required("Age category is required"),
          email: Yup.string()
            .email("Please enter valid email")
            .required("Email is required"),
          mobileNumber: Yup.string()
            .required("Mobile number is required")
            .matches(/^\d{10}$/, "Please enter valid mobile number"),
          landLine: Yup.string().matches(
            /^\d{8,11}$/,
            "Please enter a valid landline number"
          ),
          imageUrl: Yup.string().test(
            "imageRequired",
            "Please upload image",
            function () {
              const [{ value: current = null } = {}] = this.from || [];
              return !!(current?.image || current?.imageUrl);
            }
          ),
        }),
      }),
      Yup.object({
        documents: getDocumentsValidationSchema(),
      }),
      Yup.object({
        declaration: Yup.object({
          isConsumerDuesOutStanding: Yup.boolean(),
          consumerDuesOutstanding: Yup.string().when(
            "isConsumerDuesOutstanding",
            {
              is: true,
              then: (schema) => getConsumerNumberValidation(schema),
              otherwise: (schema) => schema,
            }
          ),
          isPremisesDuesOutstanding: Yup.boolean(),
          premisesDuesOutstanding: Yup.string().when(
            "isPremisesDuesOutstanding",
            {
              is: true,
              then: (schema) => getConsumerNumberValidation(schema),
              otherwise: (schema) => schema,
            }
          ),
          isFirmDuesOutstanding: Yup.boolean(),
          firmDuesOutstanding: Yup.string().when("isFirmDuesOutstanding", {
            is: true,
            then: (schema) => getConsumerNumberValidation(schema),
            otherwise: (schema) => schema,
          }),
        }),
      }),
    ],
    [
      formData.utilityAddress?.premiseTypeId,
      formData.connectionLoad.tention,
      premiseIdsForHP,
      premiseIdsForTimeShift,
    ]
  );

  const getValidationSchema = useCallback(
    (index) => validationSchema[index],
    [validationSchema]
  );
  return getValidationSchema;
};

export default useValidationSchema;
